// Import general mixin for shared functionality.
import mixin from './mixin.js';

// Import general functions for shared functionality.
import constants from '../constants.js';

// Import axios for API consuming in a clean interface.
import axios from 'axios';

// Import Swiper for slider functionality.
import Swiper from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';
Swiper.use([Pagination, Navigation]);

// Import DatePicker for dates functionality.
import Datepicker from 'vanillajs-datepicker/Datepicker';
import nl from 'vanillajs-datepicker/locales/nl';
Object.assign(Datepicker.locales, nl);

const datePickerOptions = {
    autohide: true,
    format: 'dd-mm-yyyy',
    nextArrow: '<i class="fa-solid fa-angle-right"></i>',
    prevArrow: '<i class="fa-solid fa-angle-left"></i>',
    orientation: 'bottom',
    clearButton: true
}

const today = new Date().toISOString().split('T')[0];

const defaultApp = {
    mixins: [mixin],
    data() {
        return {
            dataApi: '',
            dataLimit: 10, // Default limit in BE
            dataSortKey: '',
            dataSortDirection: '',
            dataAmountsSentence: '',
            dataWidget: false,
            dataOverruleIds: [],
            dataStartDate: '',
            dataEndDate: '',
            dateFilterName: '',
            culture: 'nl-NL',
            language: 'nl',
            delimiter: '-',
            loading: false,
            loadmore: false,
            data: {
                items: [],
                facets: [],
                queryCount: 0,
            },
            page: 1,
            searchText: '',
            tagActive: [],
            searchText: '',
            constants: constants,
            datepicker: null,
            dayfilter: false,
            filterDate: '',
            titles: ['Agenda', 'Dagagenda', 'Avondagenda'],
            slider: null,
            sliderInstance: null
        }
    },
    // The computed object holds checks for facets set, filters chosen and filters active.
    // Furthermore, reactive properties for orderItems, filterItems and the queryModel are kept track of.
    computed: {
        facetsSet() {
            return this.data.facets.length;
        },
        itemsSet() {
            return this.data.items.length;
        },
        filtersChosen() {
            return (this.facetsSet && this.data.facets.some(facet => facet.values.length)) || this.dataOverruleIds.length || this.dataStartDate || this.dataEndDate;
        },
        filtersActive() {
            return (this.facetsSet && this.data.facets.some(facet => facet.selected)) || this.searchText;
        },
        orderItems() {
            if (!this.dataSortKey && !this.dataSortDirection) return [];
            return [
                {
                    Key: this.dataSortKey,
                    Direction: this.dataSortDirection,
                }
            ]
        },
        filterItems() {
            if (!this.filtersChosen) return [];
            if (this.dataOverruleIds.length) {
                return [
                    {
                        Key: this.constants.keys.id,
                        Values: this.dataOverruleIds,
                        Operator: this.constants.operators.in,
                    }
                ]
            }
            let fc = this.data.facets.filter(f => f.values.length).map(facet => {
                let op = this.constants.operators.in;
                if (facet.alias === this.constants.keys.date) op = this.constants.operators.equals;
                //if (facet.alias === this.constants.keys.age) op = this.constants.operators.greaterThan;
                return {
                    Key: facet.alias,
                    Values: facet.values,
                    Operator: op
                }
            });
            if (this.dataStartDate) {
                let startDateFilter = {
                    Key: this.constants.keys.start,
                    Values: [this.dataStartDate],
                    Operator: this.constants.operators.greaterThan
                }
                fc.push(startDateFilter);
            }
            if (this.dataEndDate) {
                let endDateFilter = {
                    Key: this.constants.keys.start,
                    Values: [this.dataEndDate],
                    Operator: this.constants.operators.lowerThan
                }
                fc.push(endDateFilter);
            }
            return fc;
        },
        queryModel() {
            return {
                Limit: this.dataLimit,
                Offset: this.data && !this.dataWidget ? (this.page - 1) * this.dataLimit : 0,
                OrderItems: this.orderItems,
                FilterItems: this.filterItems,
                Text: this.searchText
            }
        },
        amountsSentence() {
            if (!this.data || !this.data.queryCount || !this.data.totalCount) return null;
            return this.dataAmountsSentence
                .replace(this.constants.magicStrings.queryCount, this.data.queryCount)
                .replace(this.constants.magicStrings.totalCount, this.data.totalCount);
        },
        allDays() {
            if (!this.data.facets.length) return [];
            let dateFacet = this.data.facets.find(facet => facet.alias === this.constants.keys.date);
            if (!dateFacet) return [];
            let dates = dateFacet.options.filter(option => option.filteredAmount > 0 && option.valueAlias >= today).map(option => this.reverseDate(option.valueAlias));
            return dates;
        },
        computedTitle() {
            let dayPartFacet = this.data.facets.find(facet => facet.alias === this.constants.keys.daypart);
            if (!dayPartFacet) return this.titles[0];
            let selected = dayPartFacet.options.find(option => option.selected);
            if (!selected) return this.titles[0];
            let index = parseInt(selected.valueAlias);
            return this.titles[index];
        }
    },
    methods: {
        async init() {
            // Set loading to true.
            this.loading = true;

            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = this.$el.dataset.widget;
            if (this.$el.dataset.overruleids) this.dataOverruleIds = this.$el.dataset.overruleids.split(',');
            if (this.$el.dataset.currentculture) this.culture = this.$el.dataset.currentculture;
            this.language = this.culture.split('-')[0];
            this.delimiter = this.culture === this.constants.culture.dutch ? '-' : '/';
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api + '?culture=' + this.culture;
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (this.$el.dataset.sortkey) this.dataSortKey = this.$el.dataset.sortkey;
            if (this.$el.dataset.sortdirection) this.dataSortDirection = this.$el.dataset.sortdirection;
            if (this.$el.dataset.showxamountoftotalitems) this.dataAmountsSentence = this.$el.dataset.showxamountoftotalitems;
            if (this.$el.dataset.datefiltername) this.dateFilterName = this.$el.dataset.datefiltername;
            if (this.$el.dataset.titles) this.titles = this.$el.dataset.titles.split(',');
            if (this.$el.dataset.dayfilter) this.dayfilter = this.$el.dataset.dayfilter;
            if (this.$el.dataset.startdate) this.dataStartDate = this.$el.dataset.startdate;
            if (this.$el.dataset.enddate) this.dataEndDate = this.$el.dataset.enddate;

            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);
            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            let filterParams = urlParams.toString() ? urlParams : null;
            // Preset values for facets based on query params.
            const tempQuery = this.queryModel;
            if (filterParams) {
                let filterQuery = [];
                if (this.dataStartDate) {
                    let startDateFilter = {
                        Key: this.constants.keys.start,
                        Values: [this.dataStartDate],
                        Operator: this.constants.operators.greaterThan
                    }
                    filterQuery.push(startDateFilter);
                }
                if (this.dataEndDate) {
                    let endDateFilter = {
                        Key: this.constants.keys.start,
                        Values: [this.dataEndDate],
                        Operator: this.constants.operators.lowerThan
                    }
                    filterQuery.push(endDateFilter);
                }
                filterParams.forEach((value, key) => {
                    if (key !== 'q') {
                        filterQuery.push({ Key: key, Values: value.split(','), Operator: key === this.constants.keys.date ? this.constants.operators.equals : this.constants.operators.in });
                        if (key === this.constants.keys.date) this.filterDate = this.reverseDate(value);
                    }
                });
                tempQuery.FilterItems = filterQuery;
            }

            if (this.dayfilter) {
                tempQuery.FilterItems = [{ Key: this.constants.keys.daypart, Values: ['1'], Operator: this.constants.operators.in }];
            }

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData(this.queryModel);
                // Make sure ordering corresponds to picking order in CMS
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a, b) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
            }

            let tempLimit = this.dataLimit;
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }

            // Initial data fetch.
            const response = await this.fetchData(tempQuery);
            let replace = !this.itemsSet;
            this.setData(response, replace);
            this.dataLimit = tempLimit;

            // Remove duplicates from items.
            if (this.itemsSet) {
                this.data.items = this.removeDuplicatesById(this.data.items);
                if (this.dataWidget) this.data.items = this.data.items.slice(0, this.dataLimit);
            }

            // Set loading to false.
            this.loading = false;
        },
        // The fetchData method fetches and handles API data, mainly facets and resulting items. 
        async fetchData(query) {
            try {
                const response = await axios.post(this.dataApi, query);
                return response;
            } catch (error) {
                console.log(error);
            }
        },
        // The fetchInitialData method resets the data and page props, loads data anew and sets the data.
        async fetchInitialData(query) {
            this.page = 1;
            const response = await this.fetchData(query);
            this.setData(response, true);
            if (!this.dataWidget) this.updateUrl();
        },
        // Lazy loading data chunks handler.
        async loadMore() {
            this.loadmore = true;
            this.page++;
            const response = await this.fetchData(this.queryModel);
            this.setData(response);
            if (!this.dataWidget) this.updateUrl();
            this.loadmore = false;
        },
        reverseDate(dateString) {
            var dateStringComps = dateString.split('-');
            return dateStringComps[2] + '-' + dateStringComps[1] + '-' + dateStringComps[0]
        },
        // Method to set data items array or append items to it.
        setData(response, replace = false) {
            if (!response || response.status != 200 || !response.data.items) return;
            if (!this.itemsSet || replace) {
                this.data.items = response.data.items;
            }
            else {
                response.data.items.forEach(item => {
                    this.data.items.push(item);
                })
            }
            this.setFacets(response);
            this.$nextTick(() => {
                if (this.datepicker === null && this.$refs.datepicker) {
                    const self = this;
                    datePickerOptions.defaultViewDate = this.filterDate === '' ? this.allDays[0] : this.filterDate;
                    datePickerOptions.language = this.language;
                    datePickerOptions.format = this.culture === this.constants.culture.dutch ? 'dd-mm-yyyy' : 'dd/mm/yyyy',
                    datePickerOptions.datesDisabled = function (date) {
                        return !self.allDays.includes(new Intl.DateTimeFormat(self.culture, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date));
                    }
                    this.datepicker = new Datepicker(this.$refs.datepicker, datePickerOptions);
                    this.$refs.datepicker.addEventListener('changeDate', (e) => {
                        let filterdate = '';
                        if (e.detail.date !== undefined) {
                            self.filterDate = new Intl.DateTimeFormat('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(self.toDateObj(e.detail.date));
                            filterdate = self.reverseDate(self.filterDate).replaceAll('/','-');
                        } else {
                            self.filterDate = '';
                        }
                        const index = self.filterItems.findIndex(item => item.Key === this.constants.keys.date);
                        if (index > -1) {
                            self.filterItems.splice(index, 1);
                        }
                        if (filterdate.length) {
                            self.filterItems.push({ Key: this.constants.keys.date, Values: [filterdate], Operator: this.constants.operators.equals });
                        }
                        self.fetchInitialData(self.queryModel);
                    });
                    this.$refs.datepicker.addEventListener('change', (e) => {
                        if (e.target.value === '') {
                            const index = self.filterItems.findIndex(item => item.Key === this.constants.keys.date);
                            if (index > -1) {
                                self.filterItems.splice(index, 1);
                            }
                            self.filterDate = '';
                            self.fetchInitialData(self.queryModel);
                        }
                    });
                }
            });
        },
        // Method to set facets array.
        setFacets(response) {
            if (!response || response.status != 200 || !response.data.facets) return;
            this.data.facets = response.data.facets
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(facet => ({
                    ...facet,
                    values: facet.options.filter(option => option.selected).map(option => option.valueAlias),
                }));
            this.data.facets.forEach(facet => {
                facet.options.sort((a, b) => {
                    if (a.sortOrder === null) return 1;
                    if (b.sortOrder === null) return -1;
                    return a.sortOrder - b.sortOrder;
                });
            });
            let dateFacet = this.data.facets.find(facet => facet.alias === this.constants.keys.date);
            if (dateFacet) {
                const uniqueOptions = [...new Map(dateFacet.options.map(dfo => [dfo.valueAlias, dfo])).values()];
                dateFacet.options = uniqueOptions;
            }
            this.data.queryCount = response.data.queryCount;
            this.data.totalCount = response.data.totalCount;
        },
        // Method for removing duplicates from array by id.
        removeDuplicatesById(array) {
            return array.reduce((accumulator, current) => {
                if (!accumulator.find((item) => item.id === current.id)) {
                    accumulator.push(current);
                }
                return accumulator;
            }, []);
        },
        // Data alteration function for reloading data with set filters.
        toggleOption(facetAlias, optionAlias, single = false) {
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (single) {
                changedFacet.values = [optionAlias];
            } else {
                if (changedFacet && changedFacet.values.includes(optionAlias)) {
                    changedFacet.values = changedFacet.values.filter(value => value !== optionAlias);
                } else {
                    changedFacet.values.push(optionAlias);
                }
            }
            this.fetchInitialData(this.queryModel);
        },
        selectOption(facetAlias, event) {
            let optionAlias = event.target.value;
            let changedFacet = this.data.facets.find(facet => facet.alias === facetAlias);
            if (optionAlias === '') {
                changedFacet.values = [];
            } else {
                changedFacet.values = [optionAlias];
            }
            this.fetchInitialData(this.queryModel);
        },
        resetOption(alias) {
            this.data.facets.find(facet => facet.alias === alias).values = [];
            this.fetchInitialData(this.queryModel);
        },
        checkNoneSelected(alias) {
            return this.data.facets.find(facet => facet.alias === alias).values.length === 0;
        },
        checkHasDateFilter() {
            return this.data.facets.some(facet => facet.inputType === this.dateFilterName);
        },
        // Data alteration function for resetting all active filters and reloading data.
        reset() {
            this.data.facets.forEach(facet => facet.values = []);
            this.searchText = '';
            this.loading = true;
            this.filterDate = '';
            this.fetchInitialData(this.queryModel);
            this.loading = false;
        },
        reverseDate(dateString) {
            var dateStringComps = dateString.split('-');
            return dateStringComps[2] + this.delimiter + dateStringComps[1] + this.delimiter + dateStringComps[0]
        },
        formatDate(dateString) {
            return dateString.replaceAll('-', this.delimiter);
        },
        // Function for updating browser URL and state history with changed filters, search input or page.
        updateUrl() {
            if (!this.queryModel.Text && !this.queryModel.FilterItems.length && this.page === 1) {
                history.pushState({}, null, window.location.pathname);
                return;
            }
            const urlParams = new URLSearchParams();
            if (this.queryModel.Text) urlParams.append(this.constants.queryParams.text, this.queryModel.Text);
            if (this.queryModel.FilterItems.length) {
                let filterItems = this.queryModel.FilterItems.filter(item => { return item.Key !== this.constants.keys.start });
                filterItems.forEach(filterItem => urlParams.append(filterItem.Key, filterItem.Values.join()))
            }
            if (this.page > 1) urlParams.append(this.constants.queryParams.page, this.page);
            history.pushState({}, null, window.location.pathname + '?' + urlParams.toString());
        },
        // Check if a new month should be shown in the template and return the display value if so.
        showMonth(index, date) {
            let returnVal = null;
            if (index === 0) {
                returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
            }
            else {
                let prevEvent = this.data.items[index - 1];
                if (prevEvent) {
                    if (this.toDateObj(date).getMonth() !== this.toDateObj(prevEvent.date).getMonth() || this.toDateObj(date).getYear() !== this.toDateObj(prevEvent.date).getYear()) {
                        returnVal = new Intl.DateTimeFormat('nl-NL', { month: 'long' }).format(this.toDateObj(date)) + ' ' + this.toDateObj(date).getFullYear();
                    }
                }
            }
            return returnVal;
        },
    },
    created() {
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData(this.queryModel);
        });
    },
    updated() {
        if (this.dataWidget) {
            if (this.sliderInstance) this.sliderInstance.destroy();
            this.$nextTick(() => {
                this.slider = this.$el.querySelector('.swiper');
                if (this.slider) {
                    let self = this;
                    let $next = this.slider.querySelector('.swiper-button-next');
                    let $prev = this.slider.querySelector('.swiper-button-prev');
                    let $pagination = this.slider.querySelector('.swiper-pagination');
                    this.sliderInstance = new Swiper(this.slider, {
                        slidesPerView: 1.2,
                        spaceBetween: 24,
                        watchSlidesProgress: true,
                        breakpoints: {
                            769: {
                                spaceBetween: 24,
                                slidesPerView: self.dayfilter ? 2.2 : 2.2
                            },
                            1024: {
                                spaceBetween: 24,
                                slidesPerView: self.dayfilter ? 2.2 : 2.2
                            },
                            1420: {
                                spaceBetween: 32,
                                slidesPerView: self.dayfilter ? 2.2 : 3.2
                            }
                        },
                        pagination: {
                            el: $pagination,
                            type: 'progressbar'
                        },
                        navigation: {
                            nextEl: $next,
                            prevEl: $prev
                        }
                    });
                }
            });
        }
    },
    // Call init method in mounted hook.
    mounted() {
        this.init();
    }
};

export default defaultApp;
